import dayjs from "@/lib/dayjs";
import { getDateRangeArray } from "@/lib/dayjs/get-date-range-array";
import { getMonthRangeByDate } from "@/lib/dayjs/get-month-range-by-date";

/** 料金ソース（表示ラベル用） */
export type RateMatrixPriceSource =
	| "chillnn_room_rank"
	| "chillnn_plan"
	| "chillnn_static"
	| "room_price_free"
	| "sc_daily_rate_room"
	| "sc_daily_rate_plan_room";

/**
 * セル表示状態。
 * - unregistered: 本来設定できるが値がまだない
 * - not_applicable: 定員外など、その組み合わせでは料金が成立しない／扱えない
 */
export type DailyRateCellStatus =
	| "price"
	| "stop_sale"
	| "out_of_period"
	| "unregistered"
	| "not_applicable";

export type DailyRateCell = {
	status: DailyRateCellStatus;
	priceYen: number | null;
	displayLabel: string | null;
};

export type PlanRoomDailyRateRow = {
	id: string;
	planID: string;
	planName: string;
	isSCConnected: boolean;
	isDayTrip: boolean;
	roomID: string;
	roomName: string;
	/** ダミー部屋（日帰り専用）。プランに紐づく行にだけ立つ。部屋単体では出さない */
	isDummyRoom: boolean;
	priceSource: RateMatrixPriceSource;
	salePeriodLabel: string;
	/** 日付 → 大人人数 → セル */
	cellsByDate: Partial<Record<string, Partial<Record<number, DailyRateCell>>>>;
};

export const RATE_MATRIX_PRICE_SOURCE_LABEL: Record<RateMatrixPriceSource, string> = {
	chillnn_room_rank: "部屋料金",
	chillnn_plan: "部屋料金＋プラン料金",
	chillnn_static: "固定料金",
	room_price_free: "部屋料金無料",
	sc_daily_rate_room: "サイコンと連携した料金",
	sc_daily_rate_plan_room: "サイコンと連携した料金",
};

/** 多数派の「部屋料金＋プラン料金」は行に出さない。例外だけ左列に残す */
export const isExceptionPriceSource = (priceSource: RateMatrixPriceSource): boolean =>
	priceSource === "chillnn_static" ||
	priceSource === "room_price_free" ||
	priceSource === "sc_daily_rate_room" ||
	priceSource === "sc_daily_rate_plan_room";

/** API の salePeriodLabel は販売中／停止中。行では販売中は出さず、停止中だけ非公開にする */
export const isUnpublishedSalePeriod = (salePeriodLabel: string): boolean => salePeriodLabel !== "販売中";

export const DAILY_RATE_CELL_STATUS_LABEL: Record<DailyRateCellStatus, string> = {
	price: "—",
	stop_sale: "売止",
	out_of_period: "期間外",
	unregistered: "未設定",
	/** 定員外・料金モデル上存在しないなど。ハイフンで未設定と視覚的に区別する */
	not_applicable: "—",
};

const WEEKDAY_LABELS = ["日", "月", "火", "水", "木", "金", "土"] as const;

export const getWeekdayLabel = (date: string) => WEEKDAY_LABELS[dayjs(date).day()];

export const isSaturday = (date: string) => dayjs(date).day() === 6;

export const isSunday = (date: string) => dayjs(date).day() === 0;

export const getMonthDateHeaders = (yearMonth: string) => {
	const anchorDate = `${yearMonth}-01`;
	const { firstDay, lastDay } = getMonthRangeByDate(anchorDate);
	return getDateRangeArray({ startDate: firstDay, endDate: lastDay });
};

export const getCellForGuest = (
	row: PlanRoomDailyRateRow,
	date: string,
	adultNum: number,
): DailyRateCell | null => {
	return row.cellsByDate[date]?.[adultNum] ?? null;
};
